import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {
  CampaignAnalysisWithRunInfo,
  CampaignAnalysisRun,
  ListResult,
  CampaignAnalysisStatisticalSignificance,
  SingleResult,
} from 'utils/types';
import {addResultCases, stripUndefined} from 'utils/helpers';
import ApiService from 'redux/services/api-service';

export interface IState {
  analysisResults: ListResult<CampaignAnalysisWithRunInfo>;
  analysisRuns: ListResult<CampaignAnalysisRun>;
  analysisRun: SingleResult<CampaignAnalysisRun>;
  campaignAnalysisStatisticalSignificance: SingleResult<CampaignAnalysisStatisticalSignificance>;
}

export type CampaignAnalysisRequestParams = {
  campaign_analysis_run_id?: number;
  campaign_analysis_run__campaign_id?: number;
  item_group?: string;
  response_variable?: string;
  offset?: number;
  limit?: number;
  ordering?: string;
  remove_outliers?: boolean;
  zero_negative_sales?: boolean;
};

export const analysisEndpoints = {
  getCampaignAnalysisList: createAsyncThunk(
    'analysis/getCampaignAnalysisList',
    async (params: CampaignAnalysisRequestParams) => {
      const queryParams = new URLSearchParams(stripUndefined(params as Record<string, any>));
      const response = await ApiService.get(`/campaign_analysis_run_results_with_run_info/?${queryParams.toString()}`);
      return response.data;
    },
  ),
  getCampaignAnalysisGroupingOptions: createAsyncThunk(
    'analysis/getCampaignAnalysisGroupingOptions',
    async (params: any) => {
      const queryParams = new URLSearchParams(
        stripUndefined({
          campaign_analysis_run_id: params?.campaignAnalysisRunId,
          item_group: params?.itemGroup,
          response_variable: params?.responseVariable,
          remove_outliers: params?.removeOutliers,
          zero_negative_sales: params?.zeroNegativeSales,
        }),
      );
      const response = await ApiService.get(
        `/campaign_analysis_run_results_grouping_options/?${queryParams.toString()}`,
      );
      return response.data;
    },
  ),
  getCampaignAnalysisTotalPerformance: createAsyncThunk(
    'analysis/getCampaignAnalysisTotalPerformance',
    async (params: any, {rejectWithValue}) => {
      const urlParams = new URLSearchParams();
      params?.associatedCampaignIds?.forEach(id => urlParams.append('associated_campaign_ids', id));
      urlParams.set('item_group', params?.itemGroup);
      urlParams.set('response_variable', params?.responseVariable);
      urlParams.set('grouping', params?.grouping);
      urlParams.set('grouping_name', params?.groupingName);
      urlParams.set('compare_to', params?.compareTo);
      urlParams.set('remove_outliers', params?.removeOutliers);
      urlParams.set('zero_negative_sales', params?.zeroNegativeSales);
      try {
        const response = await ApiService.get(
          `/campaign_analysis_total_performance/${params?.mainCampaignAnalysisRunId}/?${urlParams.toString()}`,
        );
        return response.data;
      } catch (e: any) {
        return rejectWithValue(e.response?.data);
      }
    },
  ),
  getCampaignAnalysisTotalPerformanceReport: createAsyncThunk(
    'analysis/getCampaignAnalysisTotalPerformanceReport',
    async (params: any, {rejectWithValue}) => {
      const urlParams = new URLSearchParams();
      params?.associatedCampaignIds?.forEach(id => urlParams.append('associated_campaign_ids', id));
      urlParams.set('item_group', params?.itemGroup);
      urlParams.set('response_variable', params?.responseVariable);
      urlParams.set('grouping', params?.grouping);
      urlParams.set('grouping_name', params?.groupingName);
      urlParams.set('compare_to', params?.compareTo);
      urlParams.set('remove_outliers', params?.removeOutliers);
      urlParams.set('zero_negative_sales', params?.zeroNegativeSales);
      try {
        const response = await ApiService.get(
          `/campaign_analysis_total_performance_report_excel/${params?.mainCampaignAnalysisRunId}/?${urlParams.toString()}`,
          {responseType: 'blob'},
        );
        return response.data;
      } catch (e: any) {
        return rejectWithValue(e.response?.data);
      }
    },
  ),
  getCampaignAnalysisStatisticalSignificance: createAsyncThunk(
    'analysis/getCampaignAnalysisStatisticalSignificance',
    async (id: number | string) => {
      const response = await ApiService.get(`/campaign_analysis_statistical_significance/${id}/`);
      return response.data;
    },
  ),
  getCampaignStructure: createAsyncThunk('analysis/getCampaignStructure', async (params: any) => {
    const response = await ApiService.get(`/campaign_analysis_structure/${params?.campaignAnalysisRunId}/`);
    return response.data;
  }),
  getCampaignAnalysisBatchResults: createAsyncThunk('analysis/getCampaignAnalysisBatchResults', async (params: any) => {
    const searchParams = new URLSearchParams(params);
    const response = await ApiService.get(`/campaign_analysis_batch_results/?${searchParams.toString()}`);
    return response.data;
  }),
  applyCampaignAnalysisBatchConfiguration: createAsyncThunk(
    'analysis/applyCampaignAnalysisBatchConfiguration',
    async (params: {
      campaignAnalysisBatchConfigurationId: number;
      item_variables?: string[];
      store_variables?: string[];
      area_variables?: string[];
      other_variables?: string[];
      notes?: string;
    }) => {
      const {campaignAnalysisBatchConfigurationId, ...data} = params;
      const response = await ApiService.put(
        `/campaign_analysis_batch_configuration_apply/${campaignAnalysisBatchConfigurationId}/`,
        data,
      );
      return response.data;
    },
  ),
  getCampaignAnalysisRuns: createAsyncThunk(
    'dashboard/getCampaignAnalysisRuns',
    async (params: {campaign_id: string | number; limit?: number; publishable?: boolean; ordering?: string}) => {
      const queryParams = new URLSearchParams(stripUndefined(params as Record<string, any>));
      const response = await ApiService.get(`/campaign_analysis_run/?${queryParams.toString()}`);
      return response.data;
    },
  ),
  getLastCampaignAnalysisRun: createAsyncThunk(
    'analysis/getLastCampaignAnalysisRun',
    async (campaign_id: string | number) => {
      const queryParams = new URLSearchParams({campaign_id: campaign_id.toString(), ordering: '-index', limit: '1'});
      const response = await ApiService.get(`/campaign_analysis_run/?${queryParams.toString()}`);
      return response.data?.results?.[0] || null;
    },
  ),
  getCampaignAnalysisRun: createAsyncThunk('analysis/getCampaignAnalysisRun', async (id: string | number) => {
    const response = await ApiService.get(`/campaign_analysis_run/${id}/`);
    return response.data;
  }),
  updateCampaignAnalysisRun: createAsyncThunk(
    'analysis/updateCampaignAnalysisRun',
    async (
      payload: {id: string | number; notes?: string; publishable?: boolean; key_findings?: string},
      {rejectWithValue},
    ) => {
      try {
        const response = await ApiService.patch(`/campaign_analysis_run/${payload.id}/`, payload);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response.data);
      }
    },
  ),
  markAsSelectedAnalysisRun: createAsyncThunk(
    'analysis/markAsSelectedAnalysisRun',
    async (id: number, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/campaign_analysis_run/${id}/mark_as_selected/`);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response.data);
      }
    },
  ),
  generateAnalysisRunKeyFindings: createAsyncThunk(
    'analysis/generateAnalysisRunKeyFindings',
    async (id: number | string, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/campaign_analysis_run/${id}/generate_key_findings/`);
        return response.data as {llm_request_id: number; campaign_task_id: number};
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
  clearSelectedAnalysisRun: createAsyncThunk(
    'analysis/clearSelectedAnalysisRun',
    async (campaign_id: number, {rejectWithValue}) => {
      try {
        const response = await ApiService.post(`/campaign_analysis_run/clear_selected/?campaign_id=${campaign_id}`);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response.data);
      }
    },
  ),
  revertCampaignToAnalysisRun: createAsyncThunk(
    'analysis/revertCampaignToAnalysisRun',
    async (
      payload: {
        id: number;
        include_pre_period_dates: boolean;
        include_campaign_dates: boolean;
        include_num_post_campaign_weeks: boolean;
        include_item_groups: boolean;
        include_location_groups: boolean;
      },
      {rejectWithValue},
    ) => {
      try {
        const {id, ...options} = payload;
        const response = await ApiService.post(`/campaign_analysis_run/${id}/revert_campaign/`, options);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
};

export const analysisSlice = createSlice({
  name: 'analysis',
  initialState: {
    analysisResults: {as: 'idle'},
    analysisRuns: {as: 'idle'},
    analysisRun: {as: 'idle'},
    campaignAnalysisStatisticalSignificance: {as: 'idle'},
  } as IState,
  reducers: {
    clearAnalysisRuns(state) {
      state.analysisRuns = {as: 'idle'};
    },
    clearAnalysisRun(state) {
      state.analysisRun = {as: 'idle'};
    },
    clearCampaignAnalysisStatisticalSignificance(state) {
      state.campaignAnalysisStatisticalSignificance = {as: 'idle'};
    },
  },
  extraReducers: builder => {
    addResultCases(builder, analysisEndpoints.getCampaignAnalysisList, 'analysisResults');
    addResultCases(builder, analysisEndpoints.getCampaignAnalysisRuns, 'analysisRuns');
    addResultCases(builder, analysisEndpoints.getCampaignAnalysisRun, 'analysisRun');
    addResultCases(
      builder,
      analysisEndpoints.getCampaignAnalysisStatisticalSignificance,
      'campaignAnalysisStatisticalSignificance',
    );
  },
});

export const analysisActions = analysisSlice.actions;

export type AnalysisSlice = {
  [analysisSlice.name]: ReturnType<(typeof analysisSlice)['reducer']>;
};
